import { useEffect, useState, useCallback, useMemo } from "react";
import api, { apiError, mediaUrl } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, LogOut, Plus, Trash2, Pencil, Download } from "lucide-react";

const TABS = [
  { id: "reservations", label: "Réservations" },
  { id: "seances", label: "Créneaux" },
  { id: "ateliers", label: "Ateliers" },
  { id: "cadeaux", label: "Cartes & Abos" },
  { id: "clients", label: "Clients & Fidélité" },
  { id: "coeurs", label: "Coups de cœur" },
  { id: "contenu", label: "Contenu du site" },
];

const inputCls =
  "w-full rounded-xl border border-[#E5DCD3] bg-white px-4 py-2.5 text-sm text-[#201815] placeholder:text-[#8A7971]/60 focus:border-[#C85A32] focus:outline-none";

const ImageUpload = ({ value, onChange, testid }) => {
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", fd);
      onChange(data.url);
      toast.success("Photo téléversée");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex items-center gap-3">
      {value ? (
        <img src={mediaUrl(value)} alt="" className="h-12 w-12 rounded-lg object-cover border border-[#E5DCD3]" />
      ) : null}
      <label
        className={`inline-flex items-center gap-2 rounded-full border border-[#C85A32]/40 px-4 py-2 text-xs text-[#C85A32] cursor-pointer hover:bg-[#C85A32] hover:text-white transition-colors ${busy ? "opacity-50 pointer-events-none" : ""}`}
      >
        {busy ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />}
        {value ? "Changer la photo" : "Choisir une photo"}
        <input data-testid={testid} type="file" accept="image/*" className="hidden" onChange={pick} />
      </label>
    </div>
  );
};

export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem("admin_token"));
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [loginError, setLoginError] = useState("");
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("reservations");

  const [reservations, setReservations] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [workshops, setWorkshops] = useState([]);
  const [giftOrders, setGiftOrders] = useState([]);
  const [content, setContent] = useState(null);
  const [adminResa, setAdminResa] = useState({ slot: "", workshop_id: "", name: "", email: "", phone: "", seats: 2, message: "" });
  const [showCancelled, setShowCancelled] = useState(false);
  const [clientsData, setClientsData] = useState({ customers: [], referrals: [] });
  const [favorites, setFavorites] = useState([]);
  const [favForm, setFavForm] = useState({ file: null, title: "", description: "" });
  const [favUploading, setFavUploading] = useState(false);
  const [blockDayForm, setBlockDayForm] = useState({ date: "", reason: "" });
  const [blockedDays, setBlockedDays] = useState([]);

  const [sessionForm, setSessionForm] = useState({ date: "", time: "", capacity: 14 });
  const emptyWorkshop = { name: "", description: "", duration: "2h15", price: "", image_url: "", active: true };
  const [workshopForm, setWorkshopForm] = useState(emptyWorkshop);
  const [editingWorkshop, setEditingWorkshop] = useState(null);

  // Créneaux regroupés par horaire : une ligne = un horaire, capacité partagée entre ateliers
  const slots = useMemo(() => {
    const map = new Map();
    sessions.forEach((s) => {
      const key = `${s.date}|${s.time}`;
      if (!map.has(key)) map.set(key, { date: s.date, time: s.time, booked: 0, capacity: 0, breakdown: [] });
      const g = map.get(key);
      g.booked += s.booked || 0;
      g.capacity = Math.max(g.capacity, s.capacity || 14);
      g.breakdown.push({ name: s.workshop_name || "Atelier", booked: s.booked || 0 });
    });
    map.forEach((g) => g.breakdown.sort((a, b) => a.name.localeCompare(b.name, "fr")));
    return [...map.values()];
  }, [sessions]);

  // Ateliers disponibles pour le créneau choisi dans le formulaire de réservation manuelle
  const slotWorkshops = useMemo(() => {
    if (!adminResa.slot) return [];
    const [d, t] = adminResa.slot.split("|");
    return sessions
      .filter((s) => s.date === d && s.time === t)
      .map((s) => ({ id: s.workshop_id || "", name: s.workshop_name || "Atelier" }))
      .sort((a, b) => a.name.localeCompare(b.name, "fr"));
  }, [sessions, adminResa.slot]);

  // Menu atelier : tous les ateliers ayant des créneaux si aucun créneau choisi, sinon ceux du créneau
  const resaWorkshops = useMemo(() => {
    if (adminResa.slot) return slotWorkshops;
    const map = new Map();
    sessions.forEach((s) => {
      const key = s.workshop_id || "";
      if (!map.has(key)) map.set(key, s.workshop_name || "Atelier");
    });
    return [...map.entries()].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name, "fr"));
  }, [adminResa.slot, slotWorkshops, sessions]);

  const loadAll = useCallback(async () => {
    try {
      const [r, s, w, g, c, cl, fv, bd] = await Promise.all([
        api.get("/admin/reservations"),
        api.get("/admin/sessions"),
        api.get("/workshops"),
        api.get("/admin/gift-orders"),
        api.get("/content"),
        api.get("/admin/customers"),
        api.get("/admin/favorites"),
        api.get("/admin/blocked-days"),
      ]);
      setReservations(r.data);
      setSessions(s.data);
      setWorkshops(w.data);
      setGiftOrders(g.data);
      setContent(c.data);
      setClientsData(cl.data);
      setFavorites(fv.data);
      setBlockedDays(bd.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        localStorage.removeItem("admin_token");
        setToken(null);
      }
    }
  }, []);

  useEffect(() => {
    if (token) loadAll();
  }, [token, loadAll]);

  const login = async (e) => {
    e.preventDefault();
    setLoginError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", loginForm);
      localStorage.setItem("admin_token", data.token);
      setToken(data.token);
    } catch (err) {
      setLoginError(apiError(err, "Connexion impossible"));
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("admin_token");
    setToken(null);
  };

  if (!token) {
    return (
      <div data-testid="admin-login-page" className="min-h-screen bg-[#201815] flex items-center justify-center p-4">
        <form onSubmit={login} className="w-full max-w-sm rounded-3xl bg-[#FBF8F3] p-8">
          <p className="text-xs uppercase tracking-[0.25em] text-[#C85A32] mb-2">Espace pro</p>
          <h1 className="font-display text-3xl text-[#201815] mb-6">Administration</h1>
          <div className="space-y-3.5">
            <input data-testid="admin-email-input" type="email" required placeholder="Email" value={loginForm.email} onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })} className={inputCls} />
            <input data-testid="admin-password-input" type="password" required placeholder="Mot de passe" value={loginForm.password} onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })} className={inputCls} />
          </div>
          {loginError && <p data-testid="admin-login-error" className="mt-4 rounded-xl border border-[#C85A32]/40 bg-[#C85A32]/10 px-4 py-3 text-sm text-[#A84623]">{loginError}</p>}
          <button data-testid="admin-login-submit" type="submit" disabled={loading} className="mt-6 w-full rounded-full bg-[#C85A32] py-3.5 text-sm font-medium text-[#FBF8F3] hover:bg-[#A84623] transition-colors disabled:opacity-40 flex items-center justify-center gap-2">
            {loading && <Loader2 size={15} className="animate-spin" />} Se connecter
          </button>
          <a href="/" data-testid="admin-back-link" className="mt-5 block text-center text-xs text-[#8A7971] hover:text-[#C85A32]">← Retour au site</a>
        </form>
      </div>
    );
  }

  const setReservationStatus = async (r, status) => {
    try {
      await api.patch(`/admin/reservations/${r.id}`, { status });
      toast.success(status === "annulée" ? "Réservation annulée, places libérées" : "Statut mis à jour");
      loadAll();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const addAdminReservation = async (e) => {
    e.preventDefault();
    const [d, t] = (adminResa.slot || "|").split("|");
    const session = sessions.find((s) => s.date === d && s.time === t && (s.workshop_id || "") === adminResa.workshop_id);
    if (!session) {
      toast.error("Choisissez un créneau et un atelier");
      return;
    }
    try {
      await api.post("/admin/reservations", {
        session_id: session.id,
        name: adminResa.name,
        email: adminResa.email || null,
        phone: adminResa.phone,
        seats: Number(adminResa.seats),
        message: adminResa.message,
      });
      toast.success("Réservation ajoutée");
      setAdminResa({ slot: "", workshop_id: "", name: "", email: "", phone: "", seats: 2, message: "" });
      loadAll();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const blockDay = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/admin/block-day", blockDayForm);
      toast.success(
        data.sessions_removed > 0
          ? `Journée bloquée — ${data.sessions_removed} créneau(x) supprimé(s), réservations associées annulées`
          : "Journée bloquée"
      );
      setBlockDayForm({ date: "", reason: "" });
      loadAll();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const unblockDay = async (date) => {
    try {
      await api.delete(`/admin/block-day/${date}`);
      toast.success("Journée débloquée — les créneaux habituels sont recréés");
      loadAll();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const addSession = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/admin/slots", { date: sessionForm.date, time: sessionForm.time, capacity: Number(sessionForm.capacity) });
      toast.success(data.created > 0 ? `Créneau ajouté pour les ${data.created} ateliers` : "Ce créneau existait déjà");
      setSessionForm({ date: "", time: "", capacity: 14 });
      loadAll();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const deleteSlot = async (slot) => {
    try {
      await api.delete(`/admin/slots/${slot.date}/${slot.time}`);
      toast.success("Créneau supprimé (réservations associées annulées)");
      loadAll();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const saveWorkshop = async (e) => {
    e.preventDefault();
    try {
      if (editingWorkshop) {
        await api.put(`/admin/workshops/${editingWorkshop}`, workshopForm);
        toast.success("Atelier mis à jour");
      } else {
        await api.post("/admin/workshops", workshopForm);
        toast.success("Atelier ajouté");
      }
      setWorkshopForm(emptyWorkshop);
      setEditingWorkshop(null);
      loadAll();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const saveContent = async (e) => {
    e.preventDefault();
    try {
      await api.put("/admin/content", content);
      toast.success("Contenu du site mis à jour");
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const giveReward = async (email, name) => {
    if (!window.confirm(`Confirmer que l'atelier offert de ${name} a été utilisé ?\nLe compteur de visites repartira à zéro.`)) return;
    try {
      await api.post("/admin/customers/reward", { email });
      toast.success("Atelier offert marqué comme utilisé — compteur remis à zéro");
      loadAll();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const deleteReservation = async (r) => {
    if (!window.confirm(`Supprimer définitivement la réservation ${r.code} de ${r.name} ?`)) return;
    try {
      await api.delete(`/admin/reservations/${r.id}`);
      toast.success("Réservation supprimée");
      loadAll();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const exportCustomers = async () => {
    try {
      const { data } = await api.get("/admin/customers/export", { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "clients-terre-a-deux.csv";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Liste clients téléchargée");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const applyReferral = async (r) => {    try {
      await api.patch(`/admin/referrals/${r.id}`, { status: "appliquée" });
      toast.success("Remise parrainage marquée comme appliquée");
      loadAll();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const uploadFavorite = async (e) => {
    e.preventDefault();
    if (!favForm.file) {
      toast.error("Choisissez une photo");
      return;
    }
    setFavUploading(true);
    const fd = new FormData();
    fd.append("file", favForm.file);
    fd.append("title", favForm.title);
    fd.append("description", favForm.description);
    try {
      await api.post("/admin/favorites", fd);
      toast.success("Photo ajoutée aux coups de cœur");
      setFavForm({ file: null, title: "", description: "" });
      e.target.reset();
      loadAll();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setFavUploading(false);
    }
  };

  const deleteFavorite = async (f) => {
    try {
      await api.delete(`/admin/favorites/${f.id}`);
      toast.success("Photo retirée des coups de cœur");
      loadAll();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const favSrc = (f) =>
    f.image_src?.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${f.image_src}` : f.image_src;

  const DEFAULT_STEPS = [
    { title: "Choisissez votre pièce", text: "Tasses, bols, assiettes, vases, figurines… Un large assortiment de pièces brutes en faïence n'attend que votre imagination. Chaque pièce affiche son prix, tout est inclus." },
    { title: "Peignez & dégustez", text: "Installez-vous confortablement : peintures, pochoirs, éponges et pinceaux sont à disposition. Pendant ce temps, on vous sert un espresso de torréfacteur local ou un latte gourmand et une pâtisserie." },
    { title: "Nous émaillons & cuisons", text: "Vous repartez léger : nous appliquons l'émail transparent puis cuisson votre pièce à 1000°C dans notre four. Le résultat : une finition brillante, durable et adaptée au contact alimentaire." },
    { title: "Récupérez votre œuvre", text: "Sous 2 semaines, votre création est prête. On vous prévient, vous passez la chercher — et pourquoi pas en profiter pour réserver l'atelier suivant." },
  ];
  const DEFAULT_MENU = [
    { name: "Espresso du torréfacteur", price: "2,00€" },
    { name: "Latte noisette caramélisée", price: "4,50€" },
    { name: "Cappuccino de spécialité", price: "4,00€" },
    { name: "Thés & infusions bio", price: "3,50€" },
    { name: "Limonade artisanale du Gers", price: "4,00€" },
    { name: "Chocolat chaud grand cru", price: "4,50€" },
    { name: "Cookie maison", price: "3,00€" },
    { name: "Gâteau du jour", price: "4,50€" },
  ];
  const contentSteps = content?.steps?.length === 4 ? content.steps : DEFAULT_STEPS;
  const contentMenu = content?.menu_items?.length ? content.menu_items : DEFAULT_MENU;
  const setStep = (i, key, val) => setContent({ ...content, steps: contentSteps.map((st, j) => (j === i ? { ...st, [key]: val } : st)) });
  const setMenuItem = (i, key, val) => setContent({ ...content, menu_items: contentMenu.map((it, j) => (j === i ? { ...it, [key]: val } : it)) });
  const addMenuItem = () => setContent({ ...content, menu_items: [...contentMenu, { name: "", price: "" }] });
  const removeMenuItem = (i) => setContent({ ...content, menu_items: contentMenu.filter((_, j) => j !== i) });

  const DEFAULT_GIFT_CARDS = [
    { label: "Carte 20€", value: "20€" },
    { label: "Carte 35€", value: "35€" },
    { label: "Carte 50€", value: "50€" },
  ];
  const DEFAULT_SUBS = [
    { label: "Abonnement 3 ateliers — 80€", value: "80€" },
    { label: "Abonnement 5 ateliers — 100€", value: "100€" },
  ];
  const giftCardOpts = content?.gift_card_options?.length ? content.gift_card_options : DEFAULT_GIFT_CARDS;
  const subOpts = content?.subscription_options?.length ? content.subscription_options : DEFAULT_SUBS;
  const setOpt = (listKey, list) => (i, key, val) =>
    setContent({ ...content, [listKey]: list.map((o, j) => (j === i ? { ...o, [key]: val } : o)) });
  const addOpt = (listKey, list) => setContent({ ...content, [listKey]: [...list, { label: "", value: "" }] });
  const removeOpt = (listKey, list) => (i) => setContent({ ...content, [listKey]: list.filter((_, j) => j !== i) });

  const optionEditor = (title, listKey, list, testidPrefix) => (
    <div className="sm:col-span-2 border-t border-[#E5DCD3] pt-6 mt-2">
      <p className="text-xs uppercase tracking-[0.25em] text-[#C85A32] mb-4">{title}</p>
      <div className="grid gap-2.5">
        {list.map((o, i) => (
          <div key={i} className="flex gap-2 items-center">
            <input data-testid={`${testidPrefix}-${i}-label-input`} placeholder="Nom affiché (ex. Carte 20€)" value={o.label} onChange={(e) => setOpt(listKey, list)(i, "label", e.target.value)} className={inputCls} />
            <input data-testid={`${testidPrefix}-${i}-value-input`} placeholder="20€" value={o.value} onChange={(e) => setOpt(listKey, list)(i, "value", e.target.value)} className={`${inputCls} !w-28 shrink-0`} />
            <button type="button" data-testid={`${testidPrefix}-${i}-remove-btn`} onClick={() => removeOpt(listKey, list)(i)} className="h-9 w-9 shrink-0 rounded-full border border-[#E5DCD3] flex items-center justify-center text-[#8A7971] hover:border-[#C85A32] hover:text-[#C85A32] transition-colors">
              <Trash2 size={13} />
            </button>
          </div>
        ))}
        <button type="button" data-testid={`${testidPrefix}-add-btn`} onClick={() => addOpt(listKey, list)} className="mt-1 self-start rounded-full border border-[#C85A32]/40 px-4 py-2 text-xs text-[#C85A32] hover:bg-[#C85A32] hover:text-white transition-colors inline-flex items-center gap-1.5">
          <Plus size={12} /> Ajouter une formule
        </button>
      </div>
    </div>
  );

  const setGiftStatus = async (o, status) => {
    try {
      await api.patch(`/admin/gift-orders/${o.id}`, { status });
      toast.success("Statut mis à jour");
      loadAll();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const statusBadge = (s) => {
    const map = {
      "confirmée": "bg-[#E9EFE5] text-[#4E7C59]",
      "annulée": "bg-[#C85A32]/10 text-[#A84623]",
      "à régler sur place": "bg-[#D9822B]/10 text-[#D9822B]",
      "à appliquer": "bg-[#D9822B]/10 text-[#D9822B]",
      "appliquée": "bg-[#E9EFE5] text-[#4E7C59]",
      "payée": "bg-[#E9EFE5] text-[#4E7C59]",
      "remise": "bg-[#E9EFE5] text-[#4E7C59]",
    };
    return <span className={`inline-block rounded-full px-3 py-1 text-xs font-medium ${map[s] || "bg-[#F4EFEA] text-[#5C4E48]"}`}>{s}</span>;
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#F4EFEA]">
      <header className="bg-[#201815] text-[#FBF8F3]">
        <div className="mx-auto max-w-6xl px-4 py-5 flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-[0.25em] text-[#D9822B]">Espace pro</p>
            <h1 className="font-display text-2xl">{content?.brand_name || "L'Argile & Café"}</h1>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" data-testid="admin-view-site" className="text-xs text-[#FBF8F3]/60 hover:text-[#D9822B] transition-colors">Voir le site</a>
            <button data-testid="admin-logout-btn" onClick={logout} className="inline-flex items-center gap-1.5 rounded-full border border-white/20 px-4 py-2 text-xs hover:border-[#C85A32] hover:text-[#C85A32] transition-colors">
              <LogOut size={13} /> Déconnexion
            </button>
          </div>
        </div>
        <div className="mx-auto max-w-6xl px-4 flex gap-1 overflow-x-auto">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`shrink-0 rounded-t-xl px-5 py-3 text-sm transition-colors ${tab === t.id ? "bg-[#F4EFEA] text-[#201815] font-medium" : "text-[#FBF8F3]/50 hover:text-[#FBF8F3]"}`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-10">
        {tab === "reservations" && (
          <div data-testid="admin-reservations-panel">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
              <h2 className="font-display text-2xl text-[#201815]">Réservations ({reservations.filter((r) => r.status !== "annulée").length} actives)</h2>
              <label className="inline-flex items-center gap-2 text-xs text-[#8A7971] cursor-pointer select-none">
                <input type="checkbox" data-testid="admin-show-cancelled" checked={showCancelled} onChange={(e) => setShowCancelled(e.target.checked)} className="h-4 w-4 accent-[#C85A32]" />
                Afficher les annulées ({reservations.filter((r) => r.status === "annulée").length})
              </label>
            </div>

            <form onSubmit={addAdminReservation} data-testid="admin-add-reservation-form" className="rounded-2xl border border-[#E5DCD3] bg-white p-6 mb-8">
              <p className="text-xs uppercase tracking-[0.25em] text-[#C85A32] mb-4">Ajouter une réservation (téléphone / sur place)</p>
              <div className="grid sm:grid-cols-3 gap-3">
                <div className="sm:col-span-2">
                  <label className="block text-xs text-[#8A7971] mb-1.5">Créneau</label>
                  <select data-testid="admin-resa-session-select" required value={adminResa.slot} onChange={(e) => setAdminResa({ ...adminResa, slot: e.target.value, workshop_id: "" })} className={inputCls}>
                    <option value="">Choisir un créneau…</option>
                    {slots.map((s) => (
                      <option key={`${s.date}|${s.time}`} value={`${s.date}|${s.time}`}>
                        {s.date.split("-").reverse().join("/")} à {s.time} — {s.capacity - s.booked} places restantes
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-[#8A7971] mb-1.5">Atelier</label>
                  <select data-testid="admin-resa-workshop-select" required value={adminResa.workshop_id} onChange={(e) => setAdminResa({ ...adminResa, workshop_id: e.target.value })} className={inputCls}>
                    <option value="">Choisir un atelier…</option>
                    {resaWorkshops.map((w) => (
                      <option key={w.id} value={w.id}>{w.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-[#8A7971] mb-1.5">Nom & prénom</label>
                  <input data-testid="admin-resa-name-input" required minLength={2} placeholder="Marie Dupont" value={adminResa.name} onChange={(e) => setAdminResa({ ...adminResa, name: e.target.value })} className={inputCls} />
                </div>
                <div>
                  <label className="block text-xs text-[#8A7971] mb-1.5">Téléphone</label>
                  <input data-testid="admin-resa-phone-input" placeholder="06 …" value={adminResa.phone} onChange={(e) => setAdminResa({ ...adminResa, phone: e.target.value })} className={inputCls} />
                </div>
                <div>
                  <label className="block text-xs text-[#8A7971] mb-1.5">Places</label>
                  <input data-testid="admin-resa-seats-input" type="number" min={1} max={20} required value={adminResa.seats} onChange={(e) => setAdminResa({ ...adminResa, seats: e.target.value })} className={inputCls} />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-xs text-[#8A7971] mb-1.5">Email du client (optionnel — confirmation envoyée si renseigné)</label>
                  <input data-testid="admin-resa-email-input" type="email" placeholder="client@email.fr" value={adminResa.email} onChange={(e) => setAdminResa({ ...adminResa, email: e.target.value })} className={inputCls} />
                </div>
                <div>
                  <label className="block text-xs text-[#8A7971] mb-1.5">Note (optionnel)</label>
                  <input data-testid="admin-resa-message-input" placeholder="Anniversaire, EVJF…" value={adminResa.message} onChange={(e) => setAdminResa({ ...adminResa, message: e.target.value })} className={inputCls} />
                </div>
              </div>
              <button data-testid="admin-resa-submit-btn" type="submit" className="mt-5 rounded-full bg-[#C85A32] px-6 py-3 text-sm font-medium text-white hover:bg-[#A84623] transition-colors inline-flex items-center gap-2">
                <Plus size={14} /> Ajouter la réservation
              </button>
            </form>

            <div className="space-y-3">
              {reservations.filter((r) => showCancelled || r.status !== "annulée").map((r) => (
                <div key={r.id} data-testid={`admin-reservation-${r.code}`} className="rounded-2xl border border-[#E5DCD3] bg-white p-5 flex flex-wrap items-center gap-4 justify-between">
                  <div>
                    <p className="font-medium text-[#201815]">{r.name} <span className="font-mono-brand text-xs text-[#C85A32] ml-2">{r.code}</span></p>
                    <p className="text-sm text-[#5C4E48] mt-1">{r.date.split("-").reverse().join("/")} à {r.time} — {r.seats} place(s){r.workshop_name ? ` · ${r.workshop_name}` : ""}</p>
                    <p className="text-xs text-[#8A7971] mt-1">{r.email} · {r.phone}{r.message ? ` · « ${r.message} »` : ""}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    {statusBadge(r.status)}
                    {r.status !== "annulée" ? (
                      <button data-testid={`admin-cancel-${r.code}`} onClick={() => setReservationStatus(r, "annulée")} className="rounded-full border border-[#C85A32]/40 px-4 py-2 text-xs text-[#A84623] hover:bg-[#C85A32] hover:text-white transition-colors">Annuler</button>
                    ) : (
                      <>
                        <button data-testid={`admin-restore-${r.code}`} onClick={() => setReservationStatus(r, "confirmée")} className="rounded-full border border-[#4E7C59]/40 px-4 py-2 text-xs text-[#4E7C59] hover:bg-[#4E7C59] hover:text-white transition-colors">Réactiver</button>
                        <button data-testid={`admin-delete-${r.code}`} onClick={() => deleteReservation(r)} title="Supprimer définitivement" className="h-9 w-9 rounded-full border border-[#E5DCD3] flex items-center justify-center text-[#8A7971] hover:border-[#C85A32] hover:text-[#C85A32] transition-colors">
                          <Trash2 size={13} />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
              {!reservations.length && <p className="text-sm text-[#8A7971]">Aucune réservation pour le moment.</p>}
            </div>
          </div>
        )}

        {tab === "seances" && (
          <div data-testid="admin-sessions-panel">
            <h2 className="font-display text-2xl text-[#201815] mb-6">Créneaux à venir ({slots.length})</h2>
            <form onSubmit={addSession} className="rounded-2xl border border-[#E5DCD3] bg-white p-6 mb-8 grid sm:grid-cols-4 gap-3 items-end">
              <div>
                <label className="block text-xs text-[#8A7971] mb-1.5">Date</label>
                <input data-testid="session-date-input" type="date" required value={sessionForm.date} onChange={(e) => setSessionForm({ ...sessionForm, date: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className="block text-xs text-[#8A7971] mb-1.5">Heure</label>
                <input data-testid="session-time-input" type="time" required value={sessionForm.time} onChange={(e) => setSessionForm({ ...sessionForm, time: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className="block text-xs text-[#8A7971] mb-1.5">Places (max 20)</label>
                <input data-testid="session-capacity-input" type="number" min={1} max={20} required value={sessionForm.capacity} onChange={(e) => setSessionForm({ ...sessionForm, capacity: e.target.value })} className={inputCls} />
              </div>
              <button data-testid="session-add-btn" type="submit" className="rounded-full bg-[#C85A32] py-3 text-sm font-medium text-white hover:bg-[#A84623] transition-colors inline-flex items-center justify-center gap-2">
                <Plus size={14} /> Ajouter
              </button>
            </form>

            <form onSubmit={blockDay} data-testid="block-day-form" className="rounded-2xl border border-[#C85A32]/30 bg-[#C85A32]/5 p-6 mb-8">
              <p className="text-xs uppercase tracking-[0.25em] text-[#C85A32] mb-1.5">Bloquer une journée entière</p>
              <p className="text-xs text-[#8A7971] mb-4">Congés, événement privé, fermeture exceptionnelle… Les créneaux du jour sont supprimés et les réservations associées annulées. La journée reste bloquée même après redéploiement.</p>
              <div className="grid sm:grid-cols-4 gap-3 items-end">
                <div>
                  <label className="block text-xs text-[#8A7971] mb-1.5">Date</label>
                  <input data-testid="block-day-date-input" type="date" required value={blockDayForm.date} onChange={(e) => setBlockDayForm({ ...blockDayForm, date: e.target.value })} className={inputCls} />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-xs text-[#8A7971] mb-1.5">Motif (optionnel)</label>
                  <input data-testid="block-day-reason-input" placeholder="Congés, privatisation, inventaire…" value={blockDayForm.reason} onChange={(e) => setBlockDayForm({ ...blockDayForm, reason: e.target.value })} className={inputCls} />
                </div>
                <button data-testid="block-day-submit-btn" type="submit" className="rounded-full bg-[#201815] py-3 text-sm font-medium text-white hover:bg-[#C85A32] transition-colors">
                  Bloquer cette journée
                </button>
              </div>
              {blockedDays.length > 0 && (
                <div className="mt-5 pt-4 border-t border-[#C85A32]/20 space-y-2">
                  {blockedDays.map((d) => (
                    <div key={d.date} data-testid={`blocked-day-${d.date}`} className="flex items-center justify-between gap-3 rounded-xl bg-white border border-[#E5DCD3] px-4 py-3">
                      <p className="text-sm text-[#201815]">
                        <span className="font-medium">{d.date.split("-").reverse().join("/")}</span>
                        {d.reason && <span className="text-xs text-[#8A7971] ml-2">— {d.reason}</span>}
                      </p>
                      <button type="button" data-testid={`unblock-day-${d.date}`} onClick={() => unblockDay(d.date)} className="rounded-full border border-[#4E7C59]/40 px-4 py-1.5 text-xs text-[#4E7C59] hover:bg-[#4E7C59] hover:text-white transition-colors">
                        Débloquer
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </form>
            <div className="space-y-2.5">
              {slots.map((s) => (
                <div key={`${s.date}|${s.time}`} data-testid={`admin-slot-${s.date}-${s.time.replace(":", "")}`} className="rounded-xl border border-[#E5DCD3] bg-white px-5 py-4 flex items-center justify-between gap-4">
                  <div>
                    <p className="text-sm text-[#201815]">
                      <span className="font-medium">{s.date.split("-").reverse().join("/")}</span> à {s.time}
                      <span className="ml-3 font-mono-brand text-xs text-[#8A7971]">{s.booked}/{s.capacity} réservées</span>
                    </p>
                    <p className="text-[11px] text-[#8A7971] mt-1">
                      {s.breakdown.map((b) => `${b.name} : ${b.booked}`).join(" · ")}
                    </p>
                  </div>
                  <button data-testid={`admin-slot-delete-${s.date}-${s.time.replace(":", "")}`} onClick={() => deleteSlot(s)} className="h-8 w-8 shrink-0 rounded-full border border-[#E5DCD3] flex items-center justify-center text-[#8A7971] hover:border-[#C85A32] hover:text-[#C85A32] transition-colors">
                    <Trash2 size={13} />
                  </button>
                </div>
              ))}
              {!slots.length && <p className="text-sm text-[#8A7971]">Aucun créneau à venir.</p>}
            </div>
          </div>
        )}

        {tab === "ateliers" && (
          <div data-testid="admin-workshops-panel">
            <h2 className="font-display text-2xl text-[#201815] mb-6">Ateliers ({workshops.length})</h2>
            <form onSubmit={saveWorkshop} className="rounded-2xl border border-[#E5DCD3] bg-white p-6 mb-8 grid sm:grid-cols-2 gap-3">
              <input data-testid="workshop-name-input" required minLength={2} placeholder="Nom de l'atelier" value={workshopForm.name} onChange={(e) => setWorkshopForm({ ...workshopForm, name: e.target.value })} className={inputCls} />
              <input data-testid="workshop-price-input" placeholder="Prix (ex : à partir de 18€)" value={workshopForm.price} onChange={(e) => setWorkshopForm({ ...workshopForm, price: e.target.value })} className={inputCls} />
              <input data-testid="workshop-duration-input" placeholder="Durée (ex : 2h15)" value={workshopForm.duration} onChange={(e) => setWorkshopForm({ ...workshopForm, duration: e.target.value })} className={inputCls} />
              <div>
                <label className="block text-xs text-[#8A7971] mb-1.5">Photo (envoyée depuis votre mobile ou ordinateur)</label>
                <ImageUpload testid="workshop-image-upload" value={workshopForm.image_url} onChange={(url) => setWorkshopForm({ ...workshopForm, image_url: url })} />
              </div>
              <textarea data-testid="workshop-description-input" rows={2} placeholder="Description" value={workshopForm.description} onChange={(e) => setWorkshopForm({ ...workshopForm, description: e.target.value })} className={`${inputCls} sm:col-span-2 resize-none`} />
              <div className="sm:col-span-2 flex gap-3">
                <button data-testid="workshop-save-btn" type="submit" className="rounded-full bg-[#C85A32] px-6 py-3 text-sm font-medium text-white hover:bg-[#A84623] transition-colors">
                  {editingWorkshop ? "Mettre à jour" : "Ajouter l'atelier"}
                </button>
                {editingWorkshop && (
                  <button type="button" data-testid="workshop-cancel-edit" onClick={() => { setEditingWorkshop(null); setWorkshopForm(emptyWorkshop); }} className="rounded-full border border-[#E5DCD3] px-6 py-3 text-sm text-[#5C4E48]">Annuler</button>
                )}
              </div>
            </form>
            <div className="grid sm:grid-cols-2 gap-4">
              {workshops.map((w) => (
                <div key={w.id} data-testid={`admin-workshop-${w.id.slice(0, 8)}`} className="rounded-2xl border border-[#E5DCD3] bg-white p-5 flex gap-4">
                  {w.image_url && <img src={mediaUrl(w.image_url)} alt="" className="h-20 w-20 rounded-xl object-cover shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-[#201815] truncate">{w.name}</p>
                    <p className="text-xs text-[#8A7971] mt-0.5">{w.duration} · {w.price}</p>
                    <div className="flex gap-2 mt-3">
                      <button data-testid={`admin-workshop-edit-${w.id.slice(0, 8)}`} onClick={() => { setEditingWorkshop(w.id); setWorkshopForm({ name: w.name, description: w.description, duration: w.duration, price: w.price, image_url: w.image_url, active: w.active }); }} className="inline-flex items-center gap-1 rounded-full border border-[#E5DCD3] px-3 py-1.5 text-xs text-[#5C4E48] hover:border-[#C85A32] hover:text-[#C85A32] transition-colors">
                        <Pencil size={11} /> Modifier
                      </button>
                      <button data-testid={`admin-workshop-delete-${w.id.slice(0, 8)}`} onClick={async () => { await api.delete(`/admin/workshops/${w.id}`); toast.success("Atelier supprimé"); loadAll(); }} className="inline-flex items-center gap-1 rounded-full border border-[#E5DCD3] px-3 py-1.5 text-xs text-[#5C4E48] hover:border-[#C85A32] hover:text-[#C85A32] transition-colors">
                        <Trash2 size={11} /> Supprimer
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "cadeaux" && (
          <div data-testid="admin-gifts-panel">
            <h2 className="font-display text-2xl text-[#201815] mb-6">Commandes cartes cadeaux & abonnements ({giftOrders.length})</h2>
            <div className="space-y-3">
              {giftOrders.map((o) => (
                <div key={o.id} data-testid={`admin-gift-${o.code}`} className="rounded-2xl border border-[#E5DCD3] bg-white p-5 flex flex-wrap items-center gap-4 justify-between">
                  <div>
                    <p className="font-medium text-[#201815]">{o.kind === "carte" ? "Carte cadeau" : "Abonnement"} — {o.option} <span className="font-mono-brand text-xs text-[#C85A32] ml-2">{o.code}</span></p>
                    <p className="text-sm text-[#5C4E48] mt-1">{o.buyer_name} · {o.buyer_email} · {o.buyer_phone}</p>
                    {o.recipient_name && <p className="text-xs text-[#8A7971] mt-1">Pour : {o.recipient_name}{o.message ? ` — « ${o.message} »` : ""}</p>}
                  </div>
                  <div className="flex items-center gap-2">
                    <a href={`/admin/carte/${o.id}`} target="_blank" rel="noopener noreferrer" data-testid={`admin-gift-print-${o.code}`} className="rounded-full border border-[#C85A32]/40 px-4 py-2 text-xs text-[#C85A32] hover:bg-[#C85A32] hover:text-white transition-colors">
                      Carte à imprimer
                    </a>
                    {statusBadge(o.status)}
                    {o.status === "à régler sur place" && (
                      <button data-testid={`admin-gift-pay-${o.code}`} onClick={() => setGiftStatus(o, "payée")} className="rounded-full border border-[#4E7C59]/40 px-4 py-2 text-xs text-[#4E7C59] hover:bg-[#4E7C59] hover:text-white transition-colors">Marquer payée</button>
                    )}
                    {o.status === "payée" && (
                      <button data-testid={`admin-gift-deliver-${o.code}`} onClick={() => setGiftStatus(o, "remise")} className="rounded-full border border-[#4E7C59]/40 px-4 py-2 text-xs text-[#4E7C59] hover:bg-[#4E7C59] hover:text-white transition-colors">Marquer remise</button>
                    )}
                  </div>
                </div>
              ))}
              {!giftOrders.length && <p className="text-sm text-[#8A7971]">Aucune commande pour le moment.</p>}
            </div>
          </div>
        )}

        {tab === "clients" && (
          <div data-testid="admin-clients-panel">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
              <h2 className="font-display text-2xl text-[#201815]">Fichier clients</h2>
              <button data-testid="admin-export-customers" onClick={exportCustomers} className="inline-flex items-center gap-2 rounded-full border border-[#C85A32]/40 px-4 py-2 text-xs font-medium text-[#C85A32] hover:bg-[#C85A32] hover:text-white transition-colors">
                <Download size={13} /> Télécharger la liste (CSV)
              </button>
            </div>
            <p className="text-sm text-[#8A7971] mb-6">
              Carte fidélité : {clientsData.settings?.loyalty_goal || 5} ateliers cumulés = {content?.loyalty_reward || "1 atelier offert d'une valeur de 20€"}. Parrainage : {content?.referral_discount || "5€"} de remise pour le parrain et le filleul, à appliquer sur place. Ces règles se règlent dans l'onglet « Contenu du site ».
            </p>

            {clientsData.customers.some((c) => c.reward_available) && (
              <div data-testid="admin-rewards-pending" className="rounded-2xl border border-[#D9822B]/30 bg-[#D9822B]/5 p-6 mb-8">
                <p className="text-xs uppercase tracking-[0.25em] text-[#A84623] mb-1.5">Récompenses à envoyer ({clientsData.customers.filter((c) => c.reward_available).length})</p>
                <p className="text-xs text-[#8A7971] mb-4">Ces clients ont gagné leur atelier offert : contactez-les par email ou téléphone pour les inviter à revenir, puis marquez la récompense comme utilisée une fois consommée.</p>
                <div className="space-y-2.5">
                  {clientsData.customers.filter((c) => c.reward_available).map((c) => (
                    <div key={c.id} data-testid={`admin-reward-pending-card-${c.referral_code}`} className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-white border border-[#E5DCD3] px-4 py-3">
                      <div>
                        <p className="text-sm font-medium text-[#201815]">{c.name}</p>
                        <p className="text-xs text-[#8A7971]">{c.email}{c.phone ? ` · ${c.phone}` : ""}</p>
                      </div>
                      <button data-testid={`admin-reward-send-${c.referral_code}`} onClick={() => giveReward(c.email, c.name)} className="rounded-full bg-[#6B7C5A] px-4 py-2 text-xs font-medium text-white hover:bg-[#5a6a4b] transition-colors">
                        Marquer comme utilisé
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-3 mb-10">
              {clientsData.customers.map((c) => (
                <div key={c.id} data-testid={`admin-customer-${c.referral_code}`} className="rounded-2xl border border-[#E5DCD3] bg-white p-5">
                  <div className="flex flex-wrap items-center gap-4 justify-between">
                    <div>
                      <p className="font-medium text-[#201815]">{c.name}</p>
                      <p className="text-xs text-[#8A7971] mt-0.5">{c.email}{c.phone ? ` · ${c.phone}` : ""}</p>
                    </div>
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="rounded-full bg-[#F4EFEA] px-3 py-1.5 text-xs font-mono-brand text-[#5C4E48]">Code parrain : {c.referral_code}</span>
                      {c.reward_available ? (
                        <>
                          <span data-testid={`admin-reward-pending-${c.referral_code}`} className="rounded-full border border-[#D9822B]/40 bg-[#D9822B]/10 px-3 py-1.5 text-xs font-medium text-[#A84623]">
                            Atelier offert non utilisé
                          </span>
                          <button data-testid={`admin-reward-${c.referral_code}`} onClick={() => giveReward(c.email, c.name)} className="rounded-full bg-[#6B7C5A] px-4 py-2 text-xs font-medium text-white hover:bg-[#5a6a4b] transition-colors">
                            Marquer comme utilisé
                          </button>
                        </>
                      ) : c.rewards_used > 0 ? (
                        <span data-testid={`admin-reward-used-${c.referral_code}`} className="rounded-full border border-[#4E7C59]/30 bg-[#4E7C59]/10 px-3 py-1.5 text-xs text-[#4E7C59]">
                          Atelier offert utilisé{c.last_reward_used_at ? ` le ${new Date(c.last_reward_used_at).toLocaleDateString("fr-FR")}` : ""}{c.rewards_used > 1 ? ` (×${c.rewards_used})` : ""}
                        </span>
                      ) : null}
                    </div>
                  </div>
                  <div className="mt-4 flex items-center gap-3">
                    <div className="flex gap-1.5">
                      {Array.from({ length: clientsData.settings?.loyalty_goal || 5 }).map((_, i) => (
                        <span key={i} className={`h-2.5 w-9 rounded-full ${i < c.fidelity_progress ? "bg-[#C85A32]" : "bg-[#EFE6DC]"}`} />
                      ))}
                    </div>
                    <span className="text-xs text-[#8A7971]">
                      {c.fidelity_progress}/{clientsData.settings?.loyalty_goal || 5} visites avant l'atelier offert · {c.visits} visite(s) au total · {c.filleuls.length} filleul(s)
                    </span>
                  </div>
                </div>
              ))}
              {!clientsData.customers.length && (
                <p className="text-sm text-[#8A7971]">Aucun client pour le moment — ils apparaissent automatiquement après chaque réservation.</p>
              )}
            </div>

            <h3 className="font-display text-xl text-[#201815] mb-4">Parrainages ({clientsData.referrals.length})</h3>
            <div className="space-y-3">
              {clientsData.referrals.map((r) => (
                <div key={r.id} data-testid={`admin-referral-${r.id.slice(0, 8)}`} className="rounded-2xl border border-[#E5DCD3] bg-white p-5 flex flex-wrap items-center gap-4 justify-between">
                  <div>
                    <p className="font-medium text-[#201815]">{r.parrain_name} → {r.filleul_name}</p>
                    <p className="text-xs text-[#8A7971] mt-0.5">Remise {r.discount} · réservation {r.reservation_code}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    {statusBadge(r.status)}
                    {r.status === "à appliquer" && (
                      <button data-testid={`admin-referral-apply-${r.id.slice(0, 8)}`} onClick={() => applyReferral(r)} className="rounded-full border border-[#4E7C59]/40 px-4 py-2 text-xs text-[#4E7C59] hover:bg-[#4E7C59] hover:text-white transition-colors">
                        Marquer appliquée
                      </button>
                    )}
                  </div>
                </div>
              ))}
              {!clientsData.referrals.length && <p className="text-sm text-[#8A7971]">Aucun parrainage pour le moment.</p>}
            </div>
          </div>
        )}

        {tab === "coeurs" && (
          <div data-testid="admin-favorites-panel">
            <h2 className="font-display text-2xl text-[#201815] mb-2">Coups de cœur du mois</h2>
            <p className="text-sm text-[#8A7971] mb-6">
              Présentez vos plus belles pièces émaillées sur le site. Ajoutez une photo depuis votre téléphone ou votre ordinateur, et changez la sélection quand vous voulez.
            </p>

            <form onSubmit={uploadFavorite} data-testid="favorite-upload-form" className="rounded-2xl border border-[#E5DCD3] bg-white p-6 mb-8 grid sm:grid-cols-4 gap-3 items-end">
              <div>
                <label className="block text-xs text-[#8A7971] mb-1.5">Photo (8 Mo max)</label>
                <input
                  data-testid="favorite-file-input"
                  type="file"
                  accept="image/*"
                  required
                  onChange={(e) => setFavForm({ ...favForm, file: e.target.files[0] || null })}
                  className="w-full text-sm text-[#5C4E48] file:mr-3 file:rounded-full file:border-0 file:bg-[#F4EFEA] file:px-4 file:py-2 file:text-xs file:font-medium file:text-[#C85A32] hover:file:bg-[#EFE6DC]"
                />
              </div>
              <div>
                <label className="block text-xs text-[#8A7971] mb-1.5">Titre</label>
                <input data-testid="favorite-title-input" placeholder="Mug fleurs sauge" value={favForm.title} onChange={(e) => setFavForm({ ...favForm, title: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className="block text-xs text-[#8A7971] mb-1.5">Description (optionnel)</label>
                <input data-testid="favorite-description-input" placeholder="Émaillage transparent, pièce client" value={favForm.description} onChange={(e) => setFavForm({ ...favForm, description: e.target.value })} className={inputCls} />
              </div>
              <button data-testid="favorite-upload-btn" type="submit" disabled={favUploading} className="rounded-full bg-[#C85A32] py-3 text-sm font-medium text-white hover:bg-[#A84623] transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-40">
                {favUploading ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Ajouter la photo
              </button>
            </form>

            <div className="grid sm:grid-cols-3 gap-4">
              {favorites.map((f, i) => (
                <div key={f.id} data-testid={`admin-favorite-${i}`} className="rounded-2xl border border-[#E5DCD3] bg-white overflow-hidden">
                  <img src={favSrc(f)} alt={f.title} className="w-full h-44 object-cover" />
                  <div className="p-4 flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-medium text-[#201815] truncate">{f.title || "Sans titre"}</p>
                      {f.description && <p className="text-xs text-[#8A7971] mt-0.5 truncate">{f.description}</p>}
                    </div>
                    <button data-testid={`admin-favorite-delete-${i}`} onClick={() => deleteFavorite(f)} className="h-8 w-8 shrink-0 rounded-full border border-[#E5DCD3] flex items-center justify-center text-[#8A7971] hover:border-[#C85A32] hover:text-[#C85A32] transition-colors">
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              ))}
              {!favorites.length && <p className="text-sm text-[#8A7971] sm:col-span-3">Aucune photo pour le moment — ajoutez votre premier coup de cœur ci-dessus.</p>}
            </div>
          </div>
        )}

        {tab === "contenu" && content && (
          <div data-testid="admin-content-panel">
            <h2 className="font-display text-2xl text-[#201815] mb-6">Contenu du site</h2>
            <form onSubmit={saveContent} className="rounded-2xl border border-[#E5DCD3] bg-white p-6 grid sm:grid-cols-2 gap-4">
              {[
                ["brand_name", "Nom du café"],
                ["hero_badge", "Badge d'annonce (hero)"],
                ["hero_line1", "Titre ligne 1"],
                ["hero_line2", "Titre ligne 2 (en italique terracotta)"],
                ["hero_line3", "Titre ligne 3"],
                ["address", "Adresse"],
                ["phone", "Téléphone"],
                ["email_contact", "Email de contact"],
                ["instagram_url", "Lien Instagram (https://instagram.com/...)"],
                ["facebook_url", "Lien Facebook (https://facebook.com/...)"],
                ["loyalty_goal", "Fidélité — nombre d'ateliers avant la récompense (ex. 5)"],
                ["loyalty_reward", "Fidélité — récompense offerte (ex. 1 atelier offert d'une valeur de 20€)"],
                ["referral_discount", "Parrainage — remise pour le parrain et le filleul (ex. 5€)"],
                ["hours", "Horaires"],
                ["hours_note", "Détail horaires (petite ligne grise en dessous)"],
                ["about_title", "Titre « à propos »"],
              ].map(([key, label]) => (
                <div key={key}>
                  <label className="block text-xs text-[#8A7971] mb-1.5">{label}</label>
                  <input data-testid={`content-${key}-input`} value={content[key] || ""} onChange={(e) => setContent({ ...content, [key]: e.target.value })} className={inputCls} />
                </div>
              ))}
              <div>
                <label className="block text-xs text-[#8A7971] mb-1.5">Logo</label>
                <ImageUpload testid="content-logo-upload" value={content.logo_url || ""} onChange={(url) => setContent({ ...content, logo_url: url })} />
              </div>
              <div>
                <label className="block text-xs text-[#8A7971] mb-1.5">Photo principale (accueil)</label>
                <ImageUpload testid="content-hero-upload" value={content.hero_image || ""} onChange={(url) => setContent({ ...content, hero_image: url })} />
              </div>
              <div>
                <label className="block text-xs text-[#8A7971] mb-1.5">Photo de la section café</label>
                <ImageUpload testid="content-cafe-upload" value={content.cafe_image || ""} onChange={(url) => setContent({ ...content, cafe_image: url })} />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs text-[#8A7971] mb-1.5">Sous-titre du hero</label>
                <textarea data-testid="content-hero-subtitle-input" rows={3} value={content.hero_subtitle || ""} onChange={(e) => setContent({ ...content, hero_subtitle: e.target.value })} className={`${inputCls} resize-none`} />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs text-[#8A7971] mb-1.5">Texte « à propos »</label>
                <textarea data-testid="content-about-text-input" rows={3} value={content.about_text || ""} onChange={(e) => setContent({ ...content, about_text: e.target.value })} className={`${inputCls} resize-none`} />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs text-[#8A7971] mb-1.5">Texte de la section café</label>
                <textarea data-testid="content-cafe-text-input" rows={3} value={content.cafe_text || ""} onChange={(e) => setContent({ ...content, cafe_text: e.target.value })} className={`${inputCls} resize-none`} placeholder="Pendant que votre pièce prend vie, notre comptoir vous sert…" />
              </div>

              <div className="sm:col-span-2 border-t border-[#E5DCD3] pt-6 mt-2">
                <p className="text-xs uppercase tracking-[0.25em] text-[#C85A32] mb-4">Les 4 chapitres de l'atelier (déroulé 2h15)</p>
                <div className="grid gap-4">
                  {contentSteps.map((st, i) => (
                    <div key={i} className="rounded-xl border border-[#E5DCD3] p-4 grid gap-2.5">
                      <p className="text-xs font-mono-brand text-[#8A7971]">Chapitre 0{i + 1}</p>
                      <input data-testid={`step-${i}-title-input`} placeholder="Titre du chapitre" value={st.title} onChange={(e) => setStep(i, "title", e.target.value)} className={inputCls} />
                      <textarea data-testid={`step-${i}-text-input`} rows={2} placeholder="Texte du chapitre" value={st.text} onChange={(e) => setStep(i, "text", e.target.value)} className={`${inputCls} resize-none`} />
                    </div>
                  ))}
                </div>
              </div>

              <div className="sm:col-span-2 border-t border-[#E5DCD3] pt-6 mt-2">
                <p className="text-xs uppercase tracking-[0.25em] text-[#C85A32] mb-4">Carte du café</p>
                <div className="grid gap-2.5">
                  {contentMenu.map((it, i) => (
                    <div key={i} className="flex gap-2 items-center">
                      <input data-testid={`menu-${i}-name-input`} placeholder="Nom (ex. Espresso)" value={it.name} onChange={(e) => setMenuItem(i, "name", e.target.value)} className={inputCls} />
                      <input data-testid={`menu-${i}-price-input`} placeholder="4,00€" value={it.price} onChange={(e) => setMenuItem(i, "price", e.target.value)} className={`${inputCls} !w-28 shrink-0`} />
                      <button type="button" data-testid={`menu-${i}-remove-btn`} onClick={() => removeMenuItem(i)} className="h-9 w-9 shrink-0 rounded-full border border-[#E5DCD3] flex items-center justify-center text-[#8A7971] hover:border-[#C85A32] hover:text-[#C85A32] transition-colors">
                        <Trash2 size={13} />
                      </button>
                    </div>
                  ))}
                  <button type="button" data-testid="menu-add-btn" onClick={addMenuItem} className="mt-1 self-start rounded-full border border-[#C85A32]/40 px-4 py-2 text-xs text-[#C85A32] hover:bg-[#C85A32] hover:text-white transition-colors inline-flex items-center gap-1.5">
                    <Plus size={12} /> Ajouter une ligne
                  </button>
                </div>
              </div>

              {optionEditor("Formules cartes cadeaux", "gift_card_options", giftCardOpts, "giftcard")}
              {optionEditor("Formules abonnements", "subscription_options", subOpts, "sub")}

              <div className="sm:col-span-2">
                <button data-testid="content-save-btn" type="submit" className="rounded-full bg-[#C85A32] px-7 py-3.5 text-sm font-medium text-white hover:bg-[#A84623] transition-colors">
                  Enregistrer les modifications
                </button>
              </div>
            </form>
          </div>
        )}
      </main>
    </div>
  );
}
